import React from 'react';
import { Lightbulb, Thermometer, Shield, Tv, Blinds, Mic } from 'lucide-react';
import { Card } from './ui/card';
import { useTranslation } from 'react-i18next';

const iconMap = {
  'Lightbulb': Lightbulb,
  'Thermometer': Thermometer,
  'Shield': Shield,
  'Tv': Tv,
  'Blinds': Blinds,
  'Mic': Mic
};

export const Features = () => {
  const { t } = useTranslation();
  
  // Get features dynamically based on current language
  const automationFeatures = [
    { id: 1, title: t('features.item1.title'), description: t('features.item1.description'), icon: "Lightbulb", image: "https://images.unsplash.com/photo-1608377205619-03a0b4c4e270", benefits: [t('features.item1.benefit1'), t('features.item1.benefit2'), t('features.item1.benefit3')] },
    { id: 2, title: t('features.item2.title'), description: t('features.item2.description'), icon: "Thermometer", image: "https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3", benefits: [t('features.item2.benefit1'), t('features.item2.benefit2'), t('features.item2.benefit3')] },
    { id: 3, title: t('features.item3.title'), description: t('features.item3.description'), icon: "Shield", image: "https://images.unsplash.com/photo-1496368077930-c1e31b4e5b44", benefits: [t('features.item3.benefit1'), t('features.item3.benefit2'), t('features.item3.benefit3')] },
    { id: 4, title: t('features.item4.title'), description: t('features.item4.description'), icon: "Tv", image: "https://images.unsplash.com/photo-1614696131965-cc559b1ecad1", benefits: [t('features.item4.benefit1'), t('features.item4.benefit2'), t('features.item4.benefit3')] },
    { id: 5, title: t('features.item5.title'), description: t('features.item5.description'), icon: "Blinds", image: "https://images.unsplash.com/photo-1751945965597-71171ec7a458", benefits: [t('features.item5.benefit1'), t('features.item5.benefit2'), t('features.item5.benefit3')] },
    { id: 6, title: t('features.item6.title'), description: t('features.item6.description'), icon: "Mic", image: "https://images.unsplash.com/photo-1752262167753-37a0ec83f614", benefits: [t('features.item6.benefit1'), t('features.item6.benefit2'), t('features.item6.benefit3')] }
  ];
  
  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#C5A35C]/10 border border-[#C5A35C]/20 mb-6">
            <span className="text-[#C5A35C] text-sm font-semibold">{t('features.badge')}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.description')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {automationFeatures.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <Card 
                key={feature.id}
                className="group relative overflow-hidden border-2 border-gray-100 hover:border-[#C5A35C]/30 transition-all duration-300 hover:shadow-2xl"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Background Image */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="relative p-8">
                  {/* Icon */}
                  <div className="mb-6 inline-flex p-4 rounded-2xl bg-gradient-to-br from-[#C5A35C]/10 to-[#C5A35C]/5 group-hover:from-[#C5A35C] group-hover:to-[#B8933D] transition-all duration-300">
                    <Icon className="w-8 h-8 text-[#C5A35C] group-hover:text-white transition-colors" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#C5A35C] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Benefits */}
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit) => (
                      <li key={`${feature.id}-${benefit}`} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-[#C5A35C] mt-1">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover Effect Border */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C5A35C] to-[#B8933D] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
